import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../../../../../context/StateContext";
import { PdfReport as ReportContent } from "../../../../../../../models/pdf.report";
import { MovementReport } from "../../../../../../../models/report.movement";
import { Filters } from "../../../../../../../models/shipeezi.table";
import { StorageBox } from "../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../models/storage.box.type";
import { User } from "../../../../../../../models/user";
import { Warehouse } from "../../../../../../../models/warehouse";
import StorageBoxService from "../../../../../../../services/storage.box.service";
import StorageBoxTypeService from "../../../../../../../services/storage.box.type.service";
import WarehouseReportService, {
  GenerateWarehouse,
  MovementsReportCSV,
} from "../../../../../../../services/warehouse.report.service";
import WarehouseService from "../../../../../../../services/warehouse.service";
import WarehousingService from "../../../../../../../services/warehousing.service";
import { FileTypes, Status, TypeReportWarehouse } from "../../../../../../../util/enums";
import { handleGeneratePdf } from "../../../../../../../util/generateReport";

export const useActions = () => {
  const { currentUser } = useAppState();

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>(0);
  const [warehousesList, setWarehousesList] = useState<Warehouse[]>([]);
  const [storageBoxTypesList, setStorageBoxTypesList] = useState<StorageBoxType[]>([]);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [filters, setFilters] = useState<Filters>();
  const [total, setTotal] = useState<number>(0);
  const [movementsReports, setMovementsReports] = useState<MovementReport[]>([]);
  const [objectSelected, setObjectSelected] = useState<StorageBox | null>(null);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const movementsFilters: GenerateWarehouse = {
    type: TypeReportWarehouse.MOVEMENTS,
    warehouses: selectedWarehouseId
      ? warehousesList.filter((warehouse) => warehouse?.id === selectedWarehouseId)
      : undefined,
    storageBoxes: objectSelected ? [objectSelected] : undefined,
    storageBoxTypes:
      filters && filters?.storageBoxType && storageBoxTypesList?.length
        ? storageBoxTypesList.filter((type) =>
            ((filters?.storageBoxType as string[]) || []).includes(type.name)
          )
        : undefined,
    drivers:
      filters && filters?.user && usersList?.length
        ? (usersList.filter((user) => {
            if (user.fullName) {
              return ((filters?.user as string[]) || []).includes(user.fullName);
            }

            return false;
          }) as User[])
        : undefined,
    startDate,
    endDate,
  };

  const getMovements = () =>
    useQuery(
      [
        "locations.status.reports.list",
        filters,
        selectedWarehouseId,
        objectSelected,
        startDate,
        endDate,
        page,
        pageSize,
      ],
      () => WarehouseReportService.getMovementsReports(movementsFilters, { page, pageSize }),
      {
        onSuccess: (response) => {
          if (response && response?.data) {
            setTotal(response.data.count);
            setMovementsReports(response.data.rows);
          }
        },
      }
    );

  const getObjects = (number?: string) =>
    StorageBoxService.getAll({
      companyId: currentUser?.companyId,
      isActive: 1,
      searchByOr: JSON.stringify([{ number: number || "" }]),
    });

  const getWarehousesList = () =>
    useQuery(
      ["warehouse.list", currentUser?.companyId, currentUser?.branches],
      () =>
        WarehouseService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "asc"]]),
          include: JSON.stringify([
            {
              association: "branches",
              where: {
                id: currentUser?.branches?.map((branch) => branch.id),
              },
            },
          ]),
        }),
      {
        enabled: !!currentUser?.companyId,
        onSuccess: (response) => {
          if (response && response.data && response.data.length > 0) {
            setWarehousesList(response.data);
            setSelectedWarehouseId(response.data[0]?.id || 0);
          }
        },
      }
    );

  const getStorageBoxTypesList = () =>
    useQuery(
      ["storage.box.types.list", currentUser?.companyId],
      () =>
        StorageBoxTypeService.getAll({
          isActive: "1",
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        onSuccess: (response) => {
          if (response && response.data) {
            setStorageBoxTypesList(response.data);
          }
        },
      }
    );

  const getUsersList = () =>
    useQuery(
      ["users.list", currentUser?.companyId],
      () =>
        WarehousingService.getAllUsersMovements({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        onSuccess: (response) => {
          if (response && response.data) {
            setUsersList(response.data as User[]);
          }
        },
      }
    );

  const useGenerateMovementsReport = () =>
    useMutation(
      ["warehouse.report.generate"],
      (generateParams: GenerateWarehouse) => WarehouseReportService.generate(generateParams),
      {
        onSuccess: (response) => {
          if (response?.data && response?.data?.content) {
            const { content } = response.data;

            switch (response?.data?.type) {
              case FileTypes.PDF:
                if (typeof content?.[0] === "number")
                  handleGeneratePdf({
                    data: response.data as ReportContent,
                    exportFile: true,
                  });
                break;
              default:
                break;
            }
          }
        },
      }
    );

  const useExportMovementsCSVData = (onSuccessCallback: (data: MovementsReportCSV[]) => void) => {
    return useMutation({
      mutationKey: ["export.movements.csv", movementsFilters],
      mutationFn: async () => {
        return WarehouseReportService.getMovementsExportCSVData(movementsFilters);
      },
      onSuccess: (response) => {
        if (response?.data?.movementsCSVData && response?.data?.movementsCSVData?.length) {
          onSuccessCallback(response.data.movementsCSVData);
        }
      },
    });
  };

  return {
    getMovements,
    getObjects,
    getStorageBoxTypesList,
    getUsersList,
    getWarehousesList,
    movementsFilters,
    movementsReports,
    objectSelected,
    page,
    pageSize,
    selectedWarehouseId,
    setEndDate,
    setFilters,
    setObjectSelected,
    setPage,
    setPageSize,
    setSelectedWarehouseId,
    setStartDate,
    storageBoxTypesList,
    total,
    useExportMovementsCSVData,
    useGenerateMovementsReport,
    usersList,
    warehousesList,
  };
};
